import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Menu,
  X,
  Search,
  ShoppingCart,
  UserCircle,
  LogOut,
  LayoutDashboard,
  User,
  LifeBuoy,
  ListPlus,
  ArrowUpCircle,
  CreditCard,
  Loader2,
  MessageSquare,
  ArrowDownCircle,
  CreditCardIcon,
  FileText,
  Info,
  Sparkles,
  ChevronRight,
  Heart,
  BellIcon,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../store/auth/authActions";
import { openAuthDialog } from "../store/auth/authSlice";
import PartsLokator from "../../public/parts_lokator_dark-removebg-preview.png";
import { useTheme } from "./ThemeProvider";
import { useDashboardData } from "@/hooks/useDashboardData";
import { fetchAiSearchParts } from "../store/aiAssistant/aiAssistantAction";
import { openChat } from "../store/aiAssistant/aiAssitantSlice";
import {
  fetchSellerById,
  listenToBuyerCount,
} from "../store/partChat/partChatActions";
import { subscribeToUnreadCount } from "@/store/partInquiry/partInquiryThunk";
import { fetchPartById } from "../store/useParts/usePartsThunk";
import {
  notificationUnreadCount,
  updateInquiryBells,
} from "../store/partInquiry/partInquiryThunk";
import {
  getEffectivePlan,
  isDowngradePending,
} from "../utils/subscriptionUtils";

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { theme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { totalBuyerUnreadMessages } = useSelector((state) => state.partChat);
  const { isAuthenticated, user, currentSubscription } = useSelector(
    (state) => state.auth,
  );
  const unsubscribeUnreadCountRef = useRef(null);
  const { unreadCount, newNotificationCount, updateLoading } = useSelector(
    (state) => state.inquries,
  );

  const { searchLoading } = useSelector((state) => state.ai);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const searchResultsRef = useRef(null);

  const previousUnreadCountRef = useRef(unreadCount);
  const audioRef = useRef(null);

  const isSeller = isAuthenticated && user?.role === "seller";
  const hasActivePlan = isSeller && currentSubscription?.status === "active";

  const { dashboardStats } = useDashboardData(isSeller && hasActivePlan);

  const plan = getEffectivePlan(currentSubscription) === "premium";

  const toggleMobileMenu = useCallback(
    () => setIsMobileMenuOpen((prev) => !prev),
    [],
  );

  const handleSearchTermChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setSearchError(null);
  }, []);

  const handleLogout = useCallback(async () => {
    await dispatch(logout()).unwrap();
    navigate("/");
    setIsMobileMenuOpen(false);
  }, [dispatch, navigate]);

  const handleNavigation = useCallback(
    (path) => {
      navigate(path);
      setIsMobileMenuOpen(false);
    },
    [navigate],
  );

  const [showHelp, setShowHelp] = useState(false);

  const playNotificationSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        const playPromise = audioRef.current.play();

        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.warn("Audio play failed:", error);
            setTimeout(() => {
              audioRef.current
                ?.play()
                .catch((e) => console.warn("Retry failed:", e));
            }, 100);
          });
        }
      }
    } catch (error) {
      console.warn("Error playing notification sound:", error);
    }
  }, []);

  const handleGlobalSearch = useCallback(
    async (e) => {
      e.preventDefault();
      setShowSearchResults(false);
      setSearchResults(null);
      setSearchError(null);

      const trimmedSearchTerm = searchTerm.trim();
      if (!trimmedSearchTerm) {
        setSearchError("Please enter a search term");
        return;
      }

      try {
        const result = await dispatch(
          fetchAiSearchParts(trimmedSearchTerm),
        ).unwrap();

        if (result.success) {
          const basicResults = {
            query: result.query,
            results: result.results || [],
            aiResponse: result.aiResponse,
            totalResults: result.totalResults,
            vehicleData: result.vin ? result?.vehicleDetails : "",
            hasResults: (result.results || []).length > 0,
          };
          setSearchResults(basicResults);
          setShowSearchResults(true);

          if (basicResults.hasResults) {
            const enrichedResults = await Promise.allSettled(
              (result.results || []).map(async (item) => {
                let sellerInfo = null;
                let partInfo = null;

                if (item?.sellerId) {
                  try {
                    sellerInfo = await dispatch(
                      fetchSellerById({ userId: item.sellerId }),
                    ).unwrap();
                  } catch (err) {
                    console.warn("Failed to fetch seller:", err);
                  }
                }

                if (item?.objectID) {
                  try {
                    partInfo = await dispatch(
                      fetchPartById(item.objectID),
                    ).unwrap();
                  } catch (err) {
                    console.warn("Failed to fetch part:", err);
                  }
                }

                return {
                  ...item,
                  sellerInfo,
                  partInfo,
                  partAvailable: !!(
                    partInfo ||
                    item.partName ||
                    item.partNumber
                  ),
                };
              }),
            );

            const successfulResults = enrichedResults
              .filter((result) => result.status === "fulfilled")
              .map((result) => result.value);

            setSearchResults((prev) => ({
              ...prev,
              results: successfulResults,
              hasResults: successfulResults.length > 0,
            }));
          }
        } else {
          setSearchError(
            "No results found. Please try a different search term.",
          );
          toast({
            variant: "destructive",
            title: "No Results Found",
            description:
              "Please try again with a different search term or be more specific.",
          });
        }
      } catch (error) {
        console.error("Error during global search:", error);
        setSearchError("Search failed. Please try again.");
        toast({
          variant: "destructive",
          title: "Search Error",
          description:
            error.message ||
            "Could not perform search due to an internal error.",
        });
      } finally {
        setSearchTerm("");
      }
    },
    [searchTerm, dispatch, toast],
  );

  const handleListInventoryClick = () => {
    // if (!isAuthenticated) {
    //   dispatch(
    //     openAuthDialog({
    //       showLoginFirstOrOnSuccess: false,
    //       onSuccessfulAuth: () => navigate("/list-inventory"),
    //       defaultTabOverride: "signup",
    //     }),
    //   );
    // } else
    if (user?.role === "seller") {
      navigate("/dashboard/add-part");
    } else {
      navigate("/list-inventory");
    }
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const handleViewSearchResults = () => {
    if (
      searchResults &&
      (searchResults.hasResults || searchResults?.vehicleData?.length > 0)
    ) {
      navigate("/ai-search-results", {
        state: {
          query: searchResults.query,
          results: searchResults.results,
          aiResponse: searchResults.aiResponse,
          totalResults: searchResults.totalResults,
          vehicleData: searchResults.vehicleData,
        },
      });
      setShowSearchResults(false);
      setSearchTerm("");
      if (isMobileMenuOpen) setIsMobileMenuOpen(false);
    }
  };

  const handleInquiryClick = () => {
    navigate("/inquire-part", {
      state: { query: searchResults?.query || searchTerm },
    });
    setShowSearchResults(false);
    setSearchTerm("");
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const handlePartClick = useCallback(
    async (partId) => {
      if (!partId) {
        toast({
          variant: "destructive",
          title: "Part Not Available",
          description: "This part is currently not available.",
        });
        return;
      }

      try {
        navigate(`/partDetail/${partId}`);
        setShowSearchResults(false);
        setSearchTerm("");
      } catch (error) {
        console.error("Error navigating to part:", error);
        toast({
          variant: "destructive",
          title: "Navigation Error",
          description: "Could not load part details. Please try again.",
        });
      }
    },
    [navigate, toast],
  );

  const navLinks = [
    {
      name: "Listings",
      path: "/listings",
      action: () => handleNavigation("/listings"),
    },
    ...(user?.role !== "buyer"
      ? [
          {
            name: "List Your Inventory",
            action: handleListInventoryClick,
          },
        ]
      : []),
    {
      name: "About Us",
      path: "/about",
      action: () => handleNavigation("/about"),
    },
    { name: "FAQ", path: "/faq", action: () => handleNavigation("/faq") },
  ];

  const UserAvatar = () => (
    <Avatar className="h-7 w-7 sm:h-8 sm:w-8 cursor-pointer ring-2 ring-offset-2 ring-offset-background ring-primary/70 hover:ring-primary transition-all">
      <AvatarImage
        src={
          user?.logoUrl ||
          user?.avatarUrl ||
          "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde"
        }
        alt={user?.name || user?.companyName || "User"}
      />
      <AvatarFallback>
        {(user?.name || user?.companyName || "U").charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );

  const handleUpgradeClick = () => {
    navigate("/list-inventory");
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target)
      ) {
        setShowHelp(false);
        setShowSearchResults(false);
        setSearchError(null);
      }
    };

    if (showSearchResults || showHelp) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showSearchResults, showHelp]);

  useEffect(() => {
    if (user?.uid) {
      dispatch(listenToBuyerCount({ userId: user.uid }));
    }
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    if (unsubscribeUnreadCountRef.current) {
      unsubscribeUnreadCountRef.current();
    }

    unsubscribeUnreadCountRef.current = subscribeToUnreadCount(
      user?.uid,
      dispatch,
    );

    return () => {
      if (unsubscribeUnreadCountRef.current) {
        unsubscribeUnreadCountRef.current();
      }
    };
  }, [user, dispatch]);

  useEffect(() => {
    if (!user?.uid || user?.role !== "seller") return;

    const unsubscribe = notificationUnreadCount(user.uid, dispatch);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user?.uid, user?.role, dispatch]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      setSearchError(null);
    };
  }, []);

  useEffect(() => {
    if (updateLoading) return;
    if (
      isAuthenticated &&
      user?.role === "seller" &&
      newNotificationCount > 0 &&
      plan
    ) {
      playNotificationSound();
      toast({
        title: "🔔 New Notification",
        description: `You have ${newNotificationCount} new ${
          newNotificationCount === 1 ? "notification" : "notifications"
        }`,
        duration: 5000,
      });

      dispatch(updateInquiryBells(user?.uid));
    }
  }, [
    newNotificationCount,
    isAuthenticated,
    user,
    playNotificationSound,
    toast,
  ]);

  return (
    <>
      {plan && (
        <audio ref={audioRef} preload="auto" src="/notification-bell.mp3" />
      )}

      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6 transition-all">
          <Link to="/">
            <img alt="PartsLokator Logo" className="h-9" src={PartsLokator} />
          </Link>

          <nav className="hidden lg:flex items-center gap-2 ml-2 mt-1 lg:gap-3">
            {navLinks.map((link) => {
              const isActive =
                link.name === "List Your Inventory"
                  ? ["/dashboard/add-part"].includes(location.pathname)
                  : location.pathname === link.path;

              return (
                <Button
                  key={link.name}
                  variant="ghost"
                  onClick={link.action}
                  className={`text-xs xl:text-sm font-medium px-3 py-2 whitespace-nowrap transition-colors
          ${
            isActive
              ? "text-accent"
              : `text-muted-foreground ${
                  theme === "dark"
                    ? "hover:text-foreground"
                    : "hover:text-white"
                }`
          }`}
                >
                  {link.name}
                </Button>
              );
            })}
          </nav>

          <form
            onSubmit={handleGlobalSearch}
            className="hidden md:flex items-center gap-2 ml-auto px-2 flex-grow max-w-xs relative"
            ref={searchResultsRef}
          >
            <div className="relative w-full">
              <Input
                type="search"
                placeholder="Search parts, VIN..."
                className="h-9 pr-20"
                value={searchTerm}
                onChange={handleSearchTermChange}
                disabled={searchLoading}
              />

              <Button
                type="submit"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-9 w-9 text-muted-foreground"
                disabled={searchLoading || searchTerm?.trim() === ""}
              >
                {searchLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowHelp(!showHelp)}
                className="absolute right-10 top-0 h-9 w-9 text-muted-foreground hover:text-primary transition-colors"
              >
                <Info className="h-4 w-4" />
              </Button>

              <AnimatePresence>
                {showHelp && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="absolute top-11 right-0 w-80 rounded-2xl border border-border/50 bg-background/95 backdrop-blur-sm shadow-xl p-4 text-sm z-50"
                  >
                    <div className="border-t border-border/40 pt-3">
                      <div className="flex items-center gap-2 mb-2.5">
                        <Sparkles className="w-4 h-4 text-yellow-500" />
                        <p className="font-semibold text-foreground text-sm tracking-tight">
                          Suggested Searches
                        </p>
                      </div>

                      <ul className="space-y-1.5">
                        {[
                          "Replacement battery for 2017 Kia Sportage",
                          "2022 BMW 3 Series side mirror",
                          "2016 Chevrolet Malibu door handle",
                          "1HGCM82633A123456 alternator",
                        ].map((suggestion, i) => (
                          <li
                            key={i}
                            className="group flex items-start gap-2 cursor-pointer transition-all hover:translate-x-1"
                          >
                            <ChevronRight className="w-3.5 h-3.5 text-primary/70 mt-1" />
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded group-hover:bg-primary/10">
                              {suggestion}
                            </code>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-4 border-t border-border/40 pt-2 text-xs text-center text-muted-foreground leading-snug">
                      💡{" "}
                      <span className="text-foreground/80 font-medium">
                        Tip:
                      </span>{" "}
                      Combine{" "}
                      <span className="font-semibold text-primary">VIN</span> +{" "}
                      <span className="font-semibold text-primary">
                        Part Name
                      </span>{" "}
                      for the most accurate results.
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <AnimatePresence>
              {showSearchResults && searchResults && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full mt-2 left-0 right-0 bg-background border border-border rounded-lg shadow-lg p-4 z-50"
                >
                  {searchError ? (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-destructive mb-1">
                            {searchError}
                          </p>
                          <Button
                            onClick={handleInquiryClick}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            Submit Part Inquiry
                            <FileText className="ml-2 h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : searchResults.hasResults ? (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 mt-1">
                          <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/40 text-primary">
                            <Search className="h-4 w-4" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground mb-1">
                            Found {searchResults.totalResults} matching part
                            {searchResults.totalResults !== 1 ? "s" : ""}
                          </p>
                          {searchResults.aiResponse && (
                            <p className="text-xs text-muted-foreground mb-2">
                              {searchResults.aiResponse}
                            </p>
                          )}

                          <div className="space-y-2 mb-3">
                            {searchResults.results
                              .slice(0, 3)
                              .map((result, index) => (
                                <div
                                  key={result.objectID || index}
                                  className="flex items-center justify-between p-2 rounded border border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                                  onClick={() =>
                                    handlePartClick(result.objectID)
                                  }
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-foreground truncate">
                                      {result.name_lowercase || "Unnamed Part"}
                                    </p>

                                    {(result.make || result.model) && (
                                      <p className="text-xs text-muted-foreground truncate">
                                        {result.make ? result.make : ""}{" "}
                                        {result.model
                                          ? `• ${result.model}`
                                          : ""}
                                      </p>
                                    )}
                                  </div>

                                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                </div>
                              ))}
                          </div>

                          <Button
                            onClick={handleViewSearchResults}
                            size="sm"
                            className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
                          >
                            View All Results ({searchResults.totalResults})
                            <Search className="ml-2 h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : searchResults?.vehicleData?.length > 0 ? (
                    <div className="flex flex-col space-y-5">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/40 text-blue-500 shadow-sm">
                            <Info className="h-4 w-4" />
                          </div>
                        </div>

                        <div className="flex-1 space-y-2">
                          <p className="text-sm font-medium text-foreground">
                            Vehicle Information Found
                          </p>
                          <p className="text-xs pb-1 text-muted-foreground">
                            {searchResults.vehicleData
                              .filter((item) =>
                                ["Make", "Model", "Model Year"].includes(
                                  item.Variable,
                                ),
                              )
                              .map((item) => item.Value)
                              .join(" ")}
                          </p>
                          <Button
                            onClick={handleViewSearchResults}
                            size="sm"
                            variant="outline"
                            className="w-full border-border/70 hover:bg-muted/30 transition-all"
                          >
                            <Info className="mr-2 h-3 w-3" />
                            View Full Vehicle Details
                          </Button>
                        </div>
                      </div>

                      {/* No Parts Found Section */}
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className="flex-shrink-0 mt-1">
                          <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gradient-to-br from-purple-500/20 to-purple-500/40 text-purple-500 shadow-sm">
                            <Info className="h-4 w-4" />
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1 space-y-2">
                          <p className="text-sm font-medium text-foreground">
                            No Parts Found for Your Search
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            Looks like we couldn't find any matching parts.
                            {user?.role === "seller"
                              ? " You can add one to your inventory or submit an inquiry."
                              : " You can submit a part inquiry to request it."}
                          </p>

                          {/* Button */}
                          {!user || user?.role === "buyer" ? (
                            <Button
                              onClick={handleInquiryClick}
                              size="sm"
                              className="w-full sm:w-auto bg-gradient-to-r from-primary to-accent text-white shadow hover:opacity-90 transition-all"
                            >
                              <FileText className="mr-2 h-4 w-4" />
                              Submit Part Inquiry
                            </Button>
                          ) : user?.role === "seller" ? (
                            <div className="flex flex-col gap-2">
                              <Button
                                onClick={handleListInventoryClick}
                                size="sm"
                                className="w-full sm:w-auto bg-gradient-to-r from-primary to-accent text-white shadow hover:opacity-90 transition-all"
                              >
                                <ListPlus className="mr-2 h-4 w-4" />
                                Add Part to Inventory
                              </Button>

                              <Button
                                onClick={handleInquiryClick}
                                size="sm"
                                variant="outline"
                                className="w-full sm:w-auto"
                              >
                                <FileText className="mr-2 h-4 w-4" />
                                Submit Part Inquiry
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground mb-1">
                            No parts found
                          </p>

                          {!user || user?.role === "buyer" ? (
                            <>
                              <p className="text-xs text-muted-foreground mb-3">
                                We couldn't find any parts matching your search.
                                Submit a part inquiry and our verified sellers
                                will help you find it.
                              </p>
                              <Button
                                onClick={handleInquiryClick}
                                variant="outline"
                                size="sm"
                                className="w-full"
                              >
                                Submit Part Inquiry
                                <FileText className="ml-2 h-3 w-3" />
                              </Button>
                            </>
                          ) : user?.role === "seller" ? (
                            <>
                              <p className="text-xs text-muted-foreground mb-3">
                                You can add one to your inventory or submit an
                                inquiry to find more buyers
                              </p>
                              <div className="flex flex-col gap-2">
                                <Button
                                  onClick={handleListInventoryClick}
                                  size="sm"
                                  className="w-full sm:w-auto bg-gradient-to-r from-primary to-accent text-white shadow hover:opacity-90 transition-all"
                                >
                                  <ListPlus className="mr-2 h-4 w-4" />
                                  Add Part to Inventory
                                </Button>

                                <Button
                                  onClick={handleInquiryClick}
                                  size="sm"
                                  variant="outline"
                                  className="w-full sm:w-auto"
                                >
                                  <FileText className="mr-2 h-4 w-4" />
                                  Submit Part Inquiry
                                </Button>
                              </div>
                            </>
                          ) : (
                            <p className="text-xs text-muted-foreground mb-2">
                              No matching results found. Please refine your
                              search.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </form>

          <div className="flex items-center gap-1 sm:gap-2">
            <ThemeToggle />
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 rounded-full p-0"
                  >
                    <UserAvatar />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {user?.companyName || user?.name}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {user?.role === "seller" && (
                    <>
                      <DropdownMenuItem
                        onClick={() => handleNavigation("/dashboard")}
                      >
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        <span>Seller Dashboard</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleNavigation("/dashboard/billing")}
                      >
                        <CreditCard className="mr-2 h-4 w-4" />
                        <span>Seller Billing</span>
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onClick={handleUpgradeClick}
                        className={`${
                          isDowngradePending(currentSubscription)
                            ? "text-primary focus:bg-primary/10 focus:text-primary"
                            : currentSubscription?.planId === "premium"
                              ? "text-accent focus:bg-accent/10 focus:text-accent"
                              : "text-primary focus:bg-primary/10 focus:text-primary"
                        }`}
                      >
                        {!isDowngradePending(currentSubscription) ? (
                          <>
                            {currentSubscription?.planId === "premium" ? (
                              <ArrowDownCircle className="mr-2 h-4 w-4" />
                            ) : (
                              <ArrowUpCircle className="mr-2 h-4 w-4" />
                            )}
                          </>
                        ) : (
                          <CreditCardIcon className="mr-2 h-4 w-4" />
                        )}
                        <span>
                          {isDowngradePending(currentSubscription)
                            ? "Update Plan"
                            : currentSubscription?.planId === "premium"
                              ? "Downgrade Plan"
                              : "Upgrade Plan"}
                        </span>
                      </DropdownMenuItem>
                      {plan && (
                        <DropdownMenuItem
                          onClick={() =>
                            handleNavigation("/profile/notifications")
                          }
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <BellIcon className="mr-2 h-4 w-4" />
                            <span>Notifications</span>
                          </div>

                          {unreadCount > 0 && (
                            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                              {unreadCount}
                            </span>
                          )}
                        </DropdownMenuItem>
                      )}
                    </>
                  )}

                  <DropdownMenuItem
                    onClick={() => handleNavigation("/profile/messages")}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      <span>Messages</span>
                    </div>

                    {(dashboardStats?.totalUnreadMessages > 0 ||
                      totalBuyerUnreadMessages > 0) && (
                      <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                        {dashboardStats?.totalUnreadMessages ||
                          totalBuyerUnreadMessages}
                      </span>
                    )}
                  </DropdownMenuItem>
                  {user?.role == "buyer" && (
                    <DropdownMenuItem
                      onClick={() => handleNavigation("/profile/saved-parts")}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center">
                        <Heart className="mr-2 h-4 w-4" />
                        <span>Saved Parts</span>
                      </div>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    onClick={() =>
                      handleNavigation(
                        user?.role === "seller"
                          ? "/dashboard/profile"
                          : "/profile",
                      )
                    }
                  >
                    <User className="mr-2 h-4 w-4" />
                    <span>My Profile</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => dispatch(openChat())}>
                    <LifeBuoy className="mr-2 h-4 w-4" />
                    <span>AI Assistant</span>
                  </DropdownMenuItem>

                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Log out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex justify-start items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() =>
                    dispatch(
                      openAuthDialog({
                        showLoginFirstOrOnSuccess: true,
                        onSuccessfulAuth: null,
                        defaultTabOverride: "login",
                      }),
                    )
                  }
                >
                  Login
                </Button>
                <Button
                  onClick={() =>
                    dispatch(
                      openAuthDialog({
                        showLoginFirstOrOnSuccess: false,
                        onSuccessfulAuth: null,
                        defaultTabOverride: "signup",
                      }),
                    )
                  }
                  className="bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90 transition-opacity whitespace-nowrap"
                >
                  Sign Up
                </Button>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={toggleMobileMenu}
            >
              {isMobileMenuOpen ? (
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              ) : (
                <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </Button>
          </div>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden border-t border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 max-h-[calc(100vh-4rem)] overflow-y-auto"
            >
              <div className="container mx-auto px-4 py-4 space-y-2">
                <form
                  onSubmit={handleGlobalSearch}
                  className="flex md:hidden items-center gap-2 mb-2"
                >
                  <div className="relative w-full">
                    <Input
                      type="search"
                      placeholder="Search parts, VIN..."
                      className="h-9 pr-10"
                      value={searchTerm}
                      onChange={handleSearchTermChange}
                      disabled={searchLoading}
                    />
                    <Button
                      type="submit"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-9 w-9 text-muted-foreground"
                      disabled={searchLoading}
                    >
                      {searchLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </form>
                {navLinks.map((link) => {
                  const isActive =
                    link.name === "List Your Inventory"
                      ? ["/list-inventory", "/dashboard/add-part"].includes(
                          location.pathname,
                        )
                      : location.pathname === link.path;

                  return (
                    <Button
                      key={link.name}
                      variant="ghost"
                      className={`w-full justify-start transition-colors
          ${
            isActive
              ? "text-accent font-semibold"
              : "text-muted-foreground hover:text-foreground"
          }`}
                      onClick={link.action}
                    >
                      {link.name}
                    </Button>
                  );
                })}

                <Button
                  variant="ghost"
                  className="w-full justify-start text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    dispatch(openChat());
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <LifeBuoy className="mr-2 h-4 w-4" /> AI Assistant
                </Button>

                <DropdownMenuSeparator />
                {isAuthenticated ? (
                  <>
                    {user?.role === "seller" && (
                      <>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-muted-foreground hover:text-foreground"
                          onClick={() => handleNavigation("/dashboard")}
                        >
                          <LayoutDashboard className="mr-2 h-4 w-4" /> Seller
                          Dashboard
                        </Button>
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-muted-foreground hover:text-foreground"
                          onClick={() => handleNavigation("/dashboard/billing")}
                        >
                          <CreditCard className="mr-2 h-4 w-4" /> Seller Billing
                        </Button>

                        <Button
                          variant="ghost"
                          className="w-full justify-start text-primary hover:text-primary"
                          onClick={() => {
                            handleUpgradeClick();
                            setIsMobileMenuOpen(false);
                          }}
                        >
                          <ArrowUpCircle className="mr-2 h-4 w-4" />
                          {plan ? "Downgrade Plan" : "Upgrade Plan"}
                        </Button>
                        {plan && (
                          <div className="flex justify-between items-center">
                            <Button
                              variant="ghost"
                              className="w-full justify-start text-muted-foreground hover:text-foreground"
                              onClick={() =>
                                handleNavigation("/profile/notifications")
                              }
                            >
                              <BellIcon className="mr-2 h-4 w-4" />{" "}
                              Notifications
                            </Button>
                            {unreadCount > 0 && (
                              <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                                {unreadCount}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    <div className="flex justify-between items-center">
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-muted-foreground hover:text-foreground"
                        onClick={() => handleNavigation("/profile/messages")}
                      >
                        <MessageSquare className="mr-2 h-4 w-4" /> Messages
                      </Button>
                      {(dashboardStats?.totalUnreadMessages > 0 ||
                        totalBuyerUnreadMessages > 0) && (
                        <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                          {dashboardStats?.totalUnreadMessages ||
                            totalBuyerUnreadMessages}
                        </span>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      className="w-full justify-start text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        handleNavigation(
                          user?.role === "seller"
                            ? "/dashboard/profile"
                            : "/profile",
                        )
                      }
                    >
                      <User className="mr-2 h-4 w-4" /> My Profile
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-destructive hover:text-destructive"
                      onClick={handleLogout}
                    >
                      <LogOut className="mr-2 h-4 w-4" /> Log out
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      className="w-full md:hidden justify-start text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        dispatch(
                          openAuthDialog({
                            showLoginFirstOrOnSuccess: true,
                            onSuccessfulAuth: null,
                            defaultTabOverride: "login",
                          }),
                        );
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Login
                    </Button>
                    <Button
                      className="w-full md:hidden bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90 transition-opacity whitespace-nowrap"
                      onClick={() => {
                        dispatch(
                          openAuthDialog({
                            showLoginFirstOrOnSuccess: false,
                            onSuccessfulAuth: null,
                            defaultTabOverride: "signup",
                          }),
                        );
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Sign Up
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Header;
